import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { config } from '../config.js';

/**
 * The only module that talks to Amazon Cognito.
 *
 * Everything above it deals in "a session exists / it does not"; the SRP
 * exchange, the refresh token and the challenge dance stay here. The library
 * keeps its own tokens in the storage we hand it, so this file also decides
 * that a closed tab ends the session.
 */

/** Tokens live in `sessionStorage`, so closing the tab signs the person out. */
const storage: ICognitoStorage = (() => {
  try {
    const probe = '__kainos_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    // Private mode or blocked storage: hold the session in memory instead, so
    // sign-in still works for as long as the page stays open.
    const memory = new Map<string, string>();
    return {
      setItem: (key, value) => void memory.set(key, value),
      getItem: (key) => memory.get(key) ?? null,
      removeItem: (key) => void memory.delete(key),
      clear: () => memory.clear(),
    };
  }
})();

let pool: CognitoUserPool | undefined;

/** Throws rather than half-working when the stage was built without Cognito ids. */
function getPool(): CognitoUserPool {
  if (!pool) {
    const { userPoolId, clientId } = config.cognito;
    if (!userPoolId || !clientId) {
      throw new Error(
        'Falta la configuración de Cognito. Revisa VITE_COGNITO_USER_POOL_ID y VITE_COGNITO_CLIENT_ID.',
      );
    }
    pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId, Storage: storage });
  }
  return pool;
}

export type SignInResult =
  | { status: 'SIGNED_IN' }
  /**
   * The person was invited and is signing in with a temporary password. They
   * are authenticated but hold no tokens yet — `completeNewPassword` finishes it.
   */
  | { status: 'NEW_PASSWORD_REQUIRED' };

/** A challenge in progress, kept between the two steps of a first sign-in. */
let pendingChallenge: CognitoUser | undefined;

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: getPool(), Storage: storage });

  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: () => {
        pendingChallenge = undefined;
        resolve({ status: 'SIGNED_IN' });
      },
      onFailure: (error) => reject(toAuthError(error)),
      newPasswordRequired: () => {
        pendingChallenge = user;
        resolve({ status: 'NEW_PASSWORD_REQUIRED' });
      },
      mfaRequired: () => reject(new Error('Esta cuenta requiere MFA, que aún no está soportado.')),
      totpRequired: () => reject(new Error('Esta cuenta requiere MFA, que aún no está soportado.')),
    });
  });
}

/**
 * Second half of a first sign-in.
 *
 * No attributes are sent back: the pool requires `email` and `name`, and both
 * are set when the person is created, so nothing is outstanding at this point.
 */
export function completeNewPassword(newPassword: string): Promise<void> {
  const user = pendingChallenge;
  if (!user) {
    return Promise.reject(new Error('No hay un cambio de contraseña pendiente.'));
  }

  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: () => {
          pendingChallenge = undefined;
          resolve();
        },
        onFailure: (error) => reject(toAuthError(error)),
      },
    );
  });
}

/**
 * The stored session, refreshed if the access token has expired.
 *
 * Resolves to `null` rather than throwing when there is nothing to restore —
 * "not signed in" is an ordinary state, not a failure.
 */
function currentSession(): Promise<CognitoUserSession | null> {
  let user: CognitoUser | null;
  try {
    user = getPool().getCurrentUser();
  } catch {
    return Promise.resolve(null);
  }
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    // `getSession` spends the refresh token when the access token is stale, so
    // a reload inside the refresh window comes back signed in.
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      resolve(error || !session?.isValid() ? null : session);
    });
  });
}

/** True when a valid (or refreshable) Cognito session exists. */
export async function hasSession(): Promise<boolean> {
  return (await currentSession()) !== null;
}

/** Bearer token for the next API call, refreshed on the way out if needed. */
export async function getAccessToken(): Promise<string | null> {
  const session = await currentSession();
  return session?.getAccessToken().getJwtToken() ?? null;
}

export function signOut(): void {
  pendingChallenge = undefined;
  try {
    getPool().getCurrentUser()?.signOut();
  } catch {
    // No pool configured, or nothing stored: there is nothing to sign out of.
  }
}

/**
 * Cognito's messages are in English and leak whether an account exists; the
 * pool has `preventUserExistenceErrors` on, so the generic case is the honest one.
 */
function toAuthError(error: unknown): Error {
  const code = (error as { code?: string } | undefined)?.code;

  switch (code) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return new Error('Correo o contraseña incorrectos.');
    case 'PasswordResetRequiredException':
      return new Error('Tu contraseña debe restablecerse. Contacta al administrador.');
    case 'UserNotConfirmedException':
      return new Error('Tu cuenta aún no está confirmada. Contacta al administrador.');
    case 'InvalidPasswordException':
      return new Error(
        'La contraseña no cumple los requisitos: mínimo 12 caracteres, con mayúsculas, minúsculas y números.',
      );
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return new Error('Demasiados intentos. Espera un momento antes de reintentar.');
    default:
      return new Error(
        (error as { message?: string } | undefined)?.message ?? 'No pudimos iniciar sesión.',
      );
  }
}
